import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import MediaEmbed from '@/components/ui/MediaEmbed'
import TagPill from '@/components/ui/TagPill'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useSEO } from '@/hooks/useSEO'
import JsonLd, { createPodcastEpisodeSchema } from '@/components/seo/JsonLd'
import type { PodcastEpisode, PodcastSeries } from '@/types'
import * as api from '@/lib/api'

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const hrs = Math.floor(mins / 60)
  if (hrs > 0) {
    return `${hrs} hour${hrs > 1 ? 's' : ''} ${mins % 60} minutes`
  }
  return `${mins} minutes`
}

function linkifyText(text: string): React.ReactNode[] {
  // Match URLs but stop at capital letters that likely start new sentences
  // or common non-URL characters
  const urlRegex = /(https?:\/\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]+?)(?=[A-Z][a-z]|[.!?]\s|Hosted|The\s|If\s|Explore|$|\s)/g
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match

  while ((match = urlRegex.exec(text)) !== null) {
    // Add text before the URL
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    // Add the URL as a link
    parts.push(
      <a
        key={match.index}
        href={match[1]}
        target="_blank"
        rel="noopener noreferrer"
        className="text-archive-gold hover:text-archive-amber underline"
      >
        {match[1]}
      </a>
    )
    lastIndex = match.index + match[1].length
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts
}

export default function PodcastDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [loading, setLoading] = useState(true)
  const [episode, setEpisode] = useState<PodcastEpisode | null>(null)
  const [series, setSeries] = useState<PodcastSeries | null>(null)

  // SEO
  useSEO({
    title: episode?.title,
    description: episode?.summary?.slice(0, 160),
    // series before archive_key: an archive can host more than one series, so
    // the archive cover is only the last resort.
    image:
      episode?.cover_image_url ||
      series?.cover_image_url ||
      (episode ? `/images/podcasts/${episode.archive_key}.jpg` : undefined),
    url: episode ? `https://thearchivenetwork.com/podcast/${episode.id}` : undefined,
    type: 'music.song',
  })

  useEffect(() => {
    async function loadData() {
      if (!id) return
      try {
        const episodeData = await api.getPodcastEpisode(id)
        setEpisode(episodeData)
        // Fetch series data for platform links
        if (episodeData.series_id) {
          const seriesData = await api.getPodcastSeriesById(episodeData.series_id)
          setSeries(seriesData)
        }
      } catch (error) {
        console.error('Failed to load episode:', error)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [id])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!episode) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-2xl text-archive-cream">Episode Not Found</h1>
          <Link to="/podcasts" className="mt-4 btn-secondary inline-block">
            Browse Podcasts
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="animate-fade-in py-16 sm:py-24">
      {/* Structured Data */}
      <JsonLd schema={createPodcastEpisodeSchema(episode, series)} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center gap-2 text-sm text-archive-muted">
            <li><Link to="/podcasts" className="hover:text-archive-gold">Podcasts</Link></li>
            <li>/</li>
            <li className="text-archive-text">{episode.title}</li>
          </ol>
        </nav>

        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <TagPill label={series?.title || episode.archive_key} variant="archive" size="md" />
            <span className="text-archive-muted">
              {new Date(episode.published_at).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-archive-cream">
            {episode.title}
          </h1>
          <p className="mt-2 text-archive-muted">
            {formatDuration(episode.duration_sec)}
          </p>
        </header>

        {/* Listen — the audio file itself. preload="none" so opening the page
            costs nothing: no audio is fetched until the visitor hits play.
            Guarded on audio_url even though every episode currently has one. */}
        {episode.audio_url && (
          <div className="mb-8">
            <h3 className="font-display text-sm text-archive-muted mb-3">Listen</h3>
            <div className="rounded-lg border border-archive-stone bg-archive-charcoal p-4">
              <audio controls preload="none" src={episode.audio_url} className="audio-player">
                Your browser does not support the audio element.{' '}
                <a href={episode.audio_url} className="link-gold">Download this episode</a>.
              </audio>
            </div>
          </div>
        )}

        {/* Player - Apple Podcasts Embed */}
        {series?.apple_podcast_id && episode.apple_episode_id && (
          <div className="mb-8">
            <MediaEmbed
              type="apple"
              applePodcastId={series.apple_podcast_id}
              appleEpisodeId={episode.apple_episode_id}
              title={episode.title}
            />
          </div>
        )}

        {/* Fallback: Spotify embed if no Apple embed available */}
        {(!series?.apple_podcast_id || !episode.apple_episode_id) && episode.spotify_url && (
          <div className="mb-8">
            <MediaEmbed
              type="spotify"
              spotifyUri={episode.spotify_url}
              title={episode.title}
            />
          </div>
        )}

        {/* Fallback: Custom embed */}
        {(!series?.apple_podcast_id || !episode.apple_episode_id) && !episode.spotify_url && episode.embed_html && (
          <div className="mb-8">
            <MediaEmbed
              type="custom"
              embedHtml={episode.embed_html}
              title={episode.title}
            />
          </div>
        )}

        {/* Also Available On */}
        {series && (series.spotify_url || series.amazon_url || series.deezer_url || series.apple_url) && (
          <div className="mb-8">
            <h3 className="font-display text-sm text-archive-muted mb-3">Also Available On</h3>
            <div className="flex flex-wrap gap-3">
              {series.spotify_url && (
                <a
                  href={series.spotify_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#1DB954] hover:bg-[#1ed760] text-white rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                  </svg>
                  Spotify
                </a>
              )}
              {series.apple_url && (
                <a
                  href={series.apple_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-b from-[#fc5c7d] to-[#6a82fb] hover:from-[#fd6d8a] hover:to-[#7b91fc] text-white rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M5.34 0A5.328 5.328 0 000 5.34v13.32A5.328 5.328 0 005.34 24h13.32A5.328 5.328 0 0024 18.66V5.34A5.328 5.328 0 0018.66 0H5.34zm6.525 2.568c2.336 0 4.448.902 6.056 2.587 1.224 1.272 1.912 2.619 2.264 4.392.12.64-.36 1.2-.96 1.2-.48 0-.84-.24-.96-.72-.264-1.272-.744-2.352-1.68-3.312-1.32-1.392-2.976-2.088-4.92-2.088-2.016 0-3.792.744-5.088 2.136-.936.984-1.464 2.088-1.68 3.264-.12.48-.528.72-.96.72-.6 0-1.08-.56-.96-1.2.336-1.68.984-3.024 2.088-4.2 1.632-1.752 3.792-2.78 6.072-2.78h.728zM12 7.992c1.2 0 2.4.384 3.312 1.2.72.648 1.2 1.368 1.464 2.232.12.456-.24.936-.72.936-.36 0-.648-.192-.792-.552-.144-.528-.456-.984-.864-1.368-.624-.552-1.416-.864-2.304-.864-.912 0-1.8.336-2.424.912-.408.384-.72.84-.864 1.32-.144.36-.432.552-.792.552-.48 0-.84-.48-.72-.936.24-.816.696-1.536 1.368-2.136.912-.84 2.16-1.296 3.336-1.296zm.12 4.632c.816 0 1.56.408 2.016 1.056.264.384.408.816.408 1.296 0 .312-.048.576-.168.84l-1.488 4.2c-.192.528-.72.888-1.296.888h-.048c-.576 0-1.104-.36-1.296-.888l-1.488-4.2c-.12-.264-.168-.528-.168-.84 0-.48.144-.912.408-1.296.456-.648 1.2-1.056 2.016-1.056h.104z"/>
                  </svg>
                  Apple Podcasts
                </a>
              )}
              {series.amazon_url && (
                <a
                  href={series.amazon_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#00A8E1] hover:bg-[#00bfff] text-white rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 4.5c2.07 0 3.75 1.68 3.75 3.75 0 .414-.336.75-.75.75s-.75-.336-.75-.75c0-1.243-1.007-2.25-2.25-2.25S9.75 7.007 9.75 8.25v3c0 .414-.336.75-.75.75s-.75-.336-.75-.75v-3C8.25 6.18 9.93 4.5 12 4.5zm-3 9c0-.414.336-.75.75-.75h4.5c.414 0 .75.336.75.75v4.5c0 .414-.336.75-.75.75h-4.5a.75.75 0 01-.75-.75v-4.5z"/>
                  </svg>
                  Amazon Music
                </a>
              )}
              {series.deezer_url && (
                <a
                  href={series.deezer_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF0092] hover:bg-[#ff1a9e] text-white rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.81 4.16v3.03H24V4.16h-5.19zM6.27 8.38v3.027h5.189V8.38h-5.19zm12.54 0v3.027H24V8.38h-5.19zM6.27 12.59v3.027h5.189v-3.03h-5.19zm6.27 0v3.027h5.19v-3.03h-5.19zm6.27 0v3.027H24v-3.03h-5.19zM0 16.81v3.029h5.19v-3.03H0zm6.27 0v3.029h5.189v-3.03h-5.19zm6.27 0v3.029h5.19v-3.03h-5.19zm6.27 0v3.029H24v-3.03h-5.19z"/>
                  </svg>
                  Deezer
                </a>
              )}
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="prose prose-invert max-w-none">
          <h2 className="font-display text-xl text-archive-cream mb-4">About This Episode</h2>
          <p className="text-archive-text leading-relaxed whitespace-pre-wrap">
            {linkifyText(episode.summary || '')}
          </p>
        </div>

        {/* Related Themes */}
        {episode.related_theme_slugs.length > 0 && (
          <div className="mt-8 pt-8 border-t border-archive-stone/50">
            <h3 className="font-display text-lg text-archive-cream mb-4">Related Themes</h3>
            <div className="flex flex-wrap gap-2">
              {episode.related_theme_slugs.map((slug) => (
                <TagPill
                  key={slug}
                  label={slug.replace(/-/g, ' ')}
                  linkTo={`/themes/${slug}`}
                  variant="theme"
                  size="md"
                />
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
